<template>
  <div class="assessment-wizard-container">
    <!-- Loading Screen -->
    <div class="assessment-loading-container">
      <div class="loading-content">
        <button
          class="restart-button"
          @click="$emit('restart')"
          aria-label="Restart search">
          <i class="material-icons-round restart-icon" aria-hidden="true">replay</i>
          <span>Restart</span>
        </button>
        <div v-if="hasError" class="error-alert">
          <i class="material-icons-round warning-icon" aria-hidden="true">warning_amber</i>
          <p
            class="error-message"
            role="alert"
            aria-live="assertive">
            We're not able to retrieve assessments. Try again later.
          </p>
        </div>
        <template v-else>
          <div class="loading-header">
            <div class="spinner-wrapper">
              <i class="fa fa-circle-o-notch fa-spin loading-spinner" aria-hidden="true" />
            </div>
            <p
              class="loading-title"
              role="status"
              aria-live="polite">
              {{ mainMessage }}
            </p>
          </div>
          <div
            class="loading-steps"
            role="status"
            aria-live="polite">
            <p
              v-for="(step, index) in progressSteps"
              :key="index"
              class="loading-step">
              {{ step }}
            </p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'AssessmentLoadingScreen',
  props: {
    hasError: {
      type: Boolean,
      default: false
    }
  },
  emits: ['restart'],
  computed: {
    ...mapGetters({
      events: 'agenticSearch/events',
      loading: 'agenticSearch/loading',
      currentPhase: 'agenticSearch/currentPhase',
    }),
    mainMessage() {
      return 'Searching 17,000+ assessments for the best matches...'
    },
    progressSteps() {
      const phase = this.currentPhase ?? 0
      const steps = [
        'Searching through our catalog of 17,000+ assessments...',
        'Filtering by grade level and subject...',
        'Ranking results by relevance...'
      ]
      // Show steps up to and including the current phase
      return steps.slice(0, phase + 1)
    },
  },
}
</script>

<style lang="less" scoped>
@import '@/variables.less';

.assessment-wizard-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--text-display-sm);
  padding: 0;
  max-width: 565.625px;
  margin: 0 auto;
}

.assessment-loading-container {
  background: @ai-gradient;
  border-radius: var(--spacing-md);
  padding: 1px;
  width: 100%;
  flex-shrink: 0;
}

.loading-content {
  background-color: #f5f5f5;
  border-radius: 7px;
  padding: var(--spacing-md) 22px 41px 41px;
  display: flex;
  flex-direction: column;
  gap: var(--text-display-sm);
  width: 100%;
  position: relative;
}

.loading-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.spinner-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
}

.loading-spinner {
  font-size: var(--text-display-sm);
  color: var(--blue-default);
}

.loading-title {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: 700;
  line-height: var(--text-display-sm);
  color: var(--gray-default);
}

.loading-steps {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.loading-step {
  margin: 0;
  font-size: var(--text-md);
  font-weight: 400;
  line-height: 20px;
  color: var(--gray-weak);
}

.error-alert {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background-color: #f6e1e0;
  border-radius: var(--radius-base);
  width: 484px;
}

.warning-icon {
  font-size: var(--text-display-sm);
  color: @gray-dark;
  flex-shrink: 0;
}

.error-message {
  margin: 0;
  font-size: var(--text-md);
  font-weight: 400;
  line-height: 20px;
  color: @gray-dark;
}

.restart-button {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: var(--spacing-xl) var(--spacing-xs);
  border: none;
  border-radius: var(--radius-base);
  background: transparent;
  color: var(--blue-default);
  font-size: var(--text-lg);
  line-height: var(--text-lg);
  cursor: pointer;
  transition: background-color 0.2s ease;
  align-self: flex-end;
  margin-bottom: -10px;

  &:hover {
    background-color: rgba(0, 125, 186, 0.05);
  }

  &:focus {
    outline: 2px solid var(--blue-default);
    outline-offset: 2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--blue-default);
    outline-offset: 2px;
  }

  .restart-icon {
    font-size: var(--text-display-sm);
    line-height: 1;
  }
}
</style>
